import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Table, Input, Button, Tag, message } from 'antd';
import { propertyHostService } from '@iso/services';

const SUCCESS_CODE = '0000';

/**
 * Manage Hosts — lists the property's owner/admin users and lets an admin set a
 * custom display label (e.g. "James D") shown to visitors instead of the full
 * name. Labels are stored per (property, user) in property_hosts.
 */
const ManageHostsModal = ({ visible, propertyId, onClose }) => {
  const [rows, setRows] = useState([]);
  const [labels, setLabels] = useState({}); // user_id -> edited label
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    if (!propertyId) {
      return;
    }
    setLoading(true);
    propertyHostService
      .getAdmins(propertyId)
      .then((res) => {
        if (res && res.code === SUCCESS_CODE) {
          const data = res.data || [];
          setRows(data);
          const initial = {};
          data.forEach((r) => { initial[r.user_id] = r.display_label || ''; });
          setLabels(initial);
        }
      })
      .finally(() => setLoading(false));
  }, [propertyId]);

  useEffect(() => {
    if (visible) {
      load();
    }
  }, [visible, load]);

  const handleSave = () => {
    // Upsert a host row per changed user: update existing, create new when a label
    // is entered for a user that has no host row yet.
    const tasks = [];
    rows.forEach((row) => {
      const nextLabel = (labels[row.user_id] || '').trim();
      const prevLabel = (row.display_label || '').trim();
      if (nextLabel === prevLabel) {
        return;
      }
      if (row.host_id) {
        tasks.push(propertyHostService.update(row.host_id, { display_label: nextLabel || null }));
      } else if (nextLabel) {
        tasks.push(propertyHostService.create({ property_id: propertyId, user_id: row.user_id, display_label: nextLabel }));
      }
    });

    if (!tasks.length) {
      message.info('No changes to save');
      onClose();
      return;
    }

    setSaving(true);
    Promise.all(tasks)
      .then((results) => {
        const ok = results.every((r) => r && r.code === SUCCESS_CODE);
        if (ok) {
          message.success('Host display labels saved');
          onClose();
        } else {
          message.error('Some labels could not be saved');
          load();
        }
      })
      .catch(() => message.error('Failed to save host labels'))
      .finally(() => setSaving(false));
  };

  const columns = [
    {
      title: 'User',
      key: 'user',
      render: (_, row) => (
        <div>
          <div>{row.name} {row.role && <Tag color={row.role === 'owner' ? 'gold' : 'blue'}>{row.role}</Tag>}</div>
          <div style={{ color: '#999', fontSize: 12 }}>{row.email}</div>
        </div>
      ),
    },
    {
      title: 'Display Label',
      key: 'display_label',
      width: 260,
      render: (_, row) => (
        <Input
          placeholder={row.name}
          value={labels[row.user_id]}
          onChange={(e) => {
            // React 16 pools synthetic events — read the value BEFORE the async
            // setState updater runs, or e.target is null by then (the clear/delete bug).
            const value = e.target.value;
            setLabels((prev) => ({ ...prev, [row.user_id]: value }));
          }}
          allowClear
        />
      ),
    },
  ];

  return (
    <Modal
      title="Manage Hosts"
      visible={visible}
      onCancel={onClose}
      width={640}
      footer={[
        <Button key="cancel" onClick={onClose}>Cancel</Button>,
        <Button key="save" type="primary" className="btn-success" loading={saving} onClick={handleSave}>Save</Button>,
      ]}
    >
      <p style={{ color: '#666', marginBottom: 12 }}>
        Set a custom display label (e.g. &quot;James D&quot;) shown to visitors instead of the user&apos;s full name.
        Leave blank to use the full name.
      </p>
      <Table
        rowKey="user_id"
        loading={loading}
        dataSource={rows}
        columns={columns}
        pagination={false}
        locale={{ emptyText: 'No admin users on this property.' }}
      />
    </Modal>
  );
};

export default ManageHostsModal;
